import { Controller } from '@hotwired/stimulus';
import { cable } from '@hotwired/turbo-rails';
import { resolveGameViewClientId } from '../lib/game_view_client';
import { buildGameViewMetricsReport } from '../lib/game_view_metrics_report';

const RESIZE_DEBOUNCE_MS = 250;
const MIN_DIMENSION_DELTA_PX = 2;
const CHANNEL_CONNECT_TIMEOUT_MS = 2000;
const CHANNEL_DISCONNECT_GRACE_MS = 3000;
const KEEPALIVE_INTERVAL_MS = 10000;

export default class extends Controller {
  static targets = ['characters', 'game'];

  static values = {
    url: String,
    campaignId: Number,
    components: Array
  };

  connect() {
    if (!this.hasUrlValue || !this.hasCampaignIdValue || !this.hasComponentsValue) return;

    this._connected = true;
    this._clientId = resolveGameViewClientId(this.campaignIdValue);
    this._resizeTimer = null;
    this._lastReport = null;
    this._channelConnected = false;
    this._useHttpFallback = false;
    this._disconnecting = false;
    this._channelTimeout = null;
    this._disconnectGraceTimeout = null;
    this._keepaliveTimer = null;
    this._subscription = null;

    this._resizeObserver = new ResizeObserver(() => this._scheduleDimensionsReport());
    this._resizeObserver.observe(this.element);
    if (this.hasGameTarget) this._resizeObserver.observe(this.gameTarget);
    if (this.hasCharactersTarget) this._resizeObserver.observe(this.charactersTarget);

    // Keep session presence active even without resize events (e.g. unfocused tab).
    this._keepaliveTimer = setInterval(() => this._reportDimensions({ force: true }), KEEPALIVE_INTERVAL_MS);

    this._connectChannel();
  }

  disconnect() {
    this._connected = false;
    this._disconnecting = true;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    if (this._resizeTimer) {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = null;
    }

    if (this._channelTimeout) {
      clearTimeout(this._channelTimeout);
      this._channelTimeout = null;
    }

    if (this._disconnectGraceTimeout) {
      clearTimeout(this._disconnectGraceTimeout);
      this._disconnectGraceTimeout = null;
    }

    if (this._keepaliveTimer) {
      clearInterval(this._keepaliveTimer);
      this._keepaliveTimer = null;
    }

    if (this._subscription) {
      this._subscription.unsubscribe();
      this._subscription = null;
    }
  }

  _scheduleDimensionsReport() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    this._resizeTimer = setTimeout(() => this._reportDimensions(), RESIZE_DEBOUNCE_MS);
  }

  componentsValueChanged() {
    if (!this._connected) return;

    this._reportDimensions({ force: true });
  }

  _reportDimensions({ force = false } = {}) {
    const dimensions = this._currentDimensions();
    if (!dimensions) return;
    const report = buildGameViewMetricsReport(dimensions, this.componentsValue);

    if (!force && this._lastReport) {
      const dimensionsUnchanged = Object.entries(dimensions).every(([key, value]) => {
        return Math.abs(this._lastReport[key] - value) < MIN_DIMENSION_DELTA_PX;
      });
      const componentsUnchanged = report.components.every(
        (component, index) => this._lastReport.components[index] === component
      );
      const unchanged =
        dimensionsUnchanged && report.components.length === this._lastReport.components.length && componentsUnchanged;
      if (unchanged) return;
    }

    this._lastReport = report;

    if (this._channelConnected && !this._useHttpFallback && this._subscription) {
      try {
        this._subscription.perform('report_dimensions', report);
        return;
      } catch (error) {
        this._switchToHttpFallback('channel perform failed', error);
      }
    }

    this._reportDimensionsViaHttp(report);
  }

  _currentDimensions() {
    const screen = this._elementDimensions(this.element);
    const game = this.hasGameTarget ? this._elementDimensions(this.gameTarget) : { width: 0, height: 0 };
    const characters = this.hasCharactersTarget
      ? this._elementDimensions(this.charactersTarget)
      : { width: 0, height: 0 };
    if (!screen || !game || !characters) return null;

    return {
      screen_width: screen.width,
      screen_height: screen.height,
      game_width: game.width,
      game_height: game.height,
      characters_width: characters.width,
      characters_height: characters.height
    };
  }

  _elementDimensions(element) {
    const rect = element.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (width <= 0 || height <= 0) return null;

    return { width, height };
  }

  _reportDimensionsViaHttp(report) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    fetch(this.urlValue, {
      method: 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': token
      },
      body: JSON.stringify({
        game_view_session: {
          client_id: this._clientId,
          ...report
        }
      })
    }).catch(() => {
      // Best effort; next resize will retry.
    });
  }

  async _connectChannel() {
    const identifier = {
      channel: 'GameViewSessionChannel',
      campaign_id: this.campaignIdValue,
      client_id: this._clientId
    };

    try {
      // Sharing Turbo's consumer guarantees its page-refresh stream subscribes before metrics can
      // promote this client and broadcast the refresh that marks the screen as live.
      const subscription = await cable.subscribeTo(identifier, {
        connected: () => {
          if (!this._connected) return;

          this._channelConnected = true;
          this._useHttpFallback = false;
          if (this._channelTimeout) {
            clearTimeout(this._channelTimeout);
            this._channelTimeout = null;
          }
          if (this._disconnectGraceTimeout) {
            clearTimeout(this._disconnectGraceTimeout);
            this._disconnectGraceTimeout = null;
          }
          this._reportDimensions({ force: true });
        },
        received: (data) => this._receiveSessionState(data),
        rejected: () => {
          if (this._connected) this._switchToHttpFallback('channel subscription rejected');
        },
        disconnected: () => {
          if (!this._connected || this._disconnecting) return;

          this._channelConnected = false;
          if (this._disconnectGraceTimeout) return;

          // Transient reconnects are common; only fallback if disconnect persists.
          this._disconnectGraceTimeout = setTimeout(() => {
            this._disconnectGraceTimeout = null;
            if (this._disconnecting || this._channelConnected) return;
            this._switchToHttpFallback('channel disconnected');
          }, CHANNEL_DISCONNECT_GRACE_MS);
        }
      });

      if (!this._connected) {
        subscription.unsubscribe();
        return;
      }

      this._subscription = subscription;
      if (!this._channelConnected) {
        this._channelTimeout = setTimeout(() => {
          if (this._channelConnected) return;

          this._switchToHttpFallback('channel connection timeout');
        }, CHANNEL_CONNECT_TIMEOUT_MS);
      }
    } catch (error) {
      if (!this._connected) return;

      this._switchToHttpFallback('channel subscription failed', error);
    }
  }

  _receiveSessionState(data) {
    if (!this._connected || data?.type !== 'session_state') return;

    this.dispatch('session-state-updated', {
      target: window,
      detail: {
        campaignId: this.campaignIdValue,
        activeClientId: data.active_client_id
      }
    });
  }

  _switchToHttpFallback(reason, error = null) {
    this._channelConnected = false;
    if (this._useHttpFallback) return;
    if (this._channelTimeout) {
      clearTimeout(this._channelTimeout);
      this._channelTimeout = null;
    }

    this._useHttpFallback = true;
    if (error) {
      console.warn(`[game-view-metrics] ${reason}`, error);
    } else {
      console.warn(`[game-view-metrics] ${reason}`);
    }

    this._reportDimensions({ force: true });
  }
}
